import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { clearCredentials } from '../store/slices/authSlice';
import { isPathAllowedForUser } from '../config/routeAccess';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, allowedRoles }) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { isAuthenticated, token, user } = useAppSelector(state => state.auth);
  const [shouldRedirect, setShouldRedirect] = useState(false);

  useEffect(() => {
    // Check localStorage for token consistency
    const storedToken = localStorage.getItem('token');

    // If Redux says authenticated but no token in localStorage, clear auth state
    if (isAuthenticated && !storedToken) {
      dispatch(clearCredentials());
      setShouldRedirect(true);
    }

    // If Redux says not authenticated but token exists in localStorage, redirect
    if (!isAuthenticated && storedToken) {
      setShouldRedirect(true);
    }

    // Listen for auth expiration events
    const handleAuthExpired = () => {
      dispatch(clearCredentials());
      setShouldRedirect(true);
    };

    window.addEventListener('authExpired', handleAuthExpired);

    return () => {
      window.removeEventListener('authExpired', handleAuthExpired);
    };
  }, [dispatch, isAuthenticated]);

  // Check if user is authenticated and has a valid token
  if (!isAuthenticated || !token || shouldRedirect) {
    return <Navigate to="/login" replace />;
  }

  if (user?.role === 'user' && !isPathAllowedForUser(location.pathname)) {
    return <Navigate to="/robots" replace />;
  }

  if (allowedRoles && (!user?.role || !allowedRoles.includes(user.role))) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
